import React, { useState, useEffect } from 'react';
import { BarChart3 } from 'lucide-react';
import { Col, Form, Row } from 'react-bootstrap';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

import ButtonBasic from '../../atoms/buttonBasic/view';
import ModalBtn from '../../molecules/modalButton/view';
import DateSelector from '../../atoms/datepicker/view';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';
import LoadingView from '../../pages/loading/loading-view';
import { DynamicForm } from '../../molecules/DynamicForm/view';
import ReportDataView from '../../molecules/ReportDataView/view';

import { getAvailableReports, generateAccountingReport } from '../../../usecases/accounting-reports-usecase';
import { EmptyArray } from '../../../domain/empty/model';
import { listener } from '../../../utils/dispatch';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { downloadAccountingExcel } from '../../../services/reports/excelGenerator';
import { downloadAccountingPdf } from '../../../services/reports/pdfGenerator';
import { userSingleton } from '../../../app/classes/User';
import './style.css';

const AccountingReportsModalButton = () => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jsonContent, setJsonContent] = useState(null);
  const [tercero, setTercero] = useState({});
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountRange, setAccountRange] = useState({ from: '', to: '' });
  const [accountMode, setAccountMode] = useState('list');
  const [selectedThird, setSelectedThird] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTaxType, setSelectedTaxType] = useState('all');
  const [includeThirds, setIncludeThirds] = useState(false);

  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [utilityAccount, setUtilityAccount] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [formData, setFormData] = useState({
    report_code: 'sales_report',
    date_from: new Date().toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
  });

  const componentsMap = {
    third: { tercero: tercero },
    account_selector: {
      selectedAccounts,
      onAccountsChange: setSelectedAccounts,
      accountRange,
      onRangeChange: setAccountRange,
      mode: accountMode,
      onModeChange: setAccountMode
    },
    third_party_search: {
      selectedThird,
      onSelect: setSelectedThird
    },
    document_type_multiselect: {
      selectedTypes,
      onSelected: setSelectedTypes
    },
    tax_type_selector: {
      selected: selectedTaxType,
      onSelect: setSelectedTaxType
    },
    include_thirds_toggle: {
      label: "¿Incluir detalle por Tercero?",
      checked: includeThirds,
      onChange: setIncludeThirds
    },
    month_year_selector: {
      month,
      onMonthChange: setMonth,
      year,
      onYearChange: setYear
    },
    utility_account_selector: {
      onSelected: setUtilityAccount,
      selected: utilityAccount
    },
    product_search_multiselect: {
      onSelected: setSelectedProducts,
      selected: selectedProducts,
      uid: userSingleton.uid,
      cid: userSingleton.cid
    }
  };

  useEffect(() => {
    listener('btn-thirds-add', (event) => {
      setTercero(event.detail.data)
      setFormData(prev => ({ ...prev, third_id: event.detail.data.id }))
      setSelectedThird(event.detail.data)
    });
  }, []);

  const fetchReports = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const availableReports = await getAvailableReports();
      if (availableReports && availableReports !== EmptyArray && availableReports.data && availableReports.data.length > 0) {
        setReports(availableReports.data);
        setFormData(prev => ({ ...prev, report_code: availableReports.data[0].id }))
      } else {
        setReports([]);
      }
    } catch (err) {
      setError('Error al cargar los reportes disponibles.');
      console.error('Error fetching available reports:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    const isSpecialReport = formData.report_code === 'balance_sheet' || formData.report_code === 'income_statement';

    if (!formData.report_code) {
      setError('Por favor, selecciona un reporte.');
      dispatchCustomEvent("loading-btn", {});
      return;
    }

    if (formData.report_code !== 'balance_sheet' && (!formData.date_from || !formData.date_to)) {
      setError('Por favor, selecciona un rango de fechas.');
      dispatchCustomEvent("loading-btn", {});
      return;
    }

    if (isSpecialReport && !utilityAccount) {
      setError('La cuenta de utilidad es obligatoria para este reporte.');
      dispatchCustomEvent("loading-btn", {});
      return;
    }
    setIsLoading(true);
    setError(null);
    setJsonContent(null);
    try {
      const requestData = {
        ...formData,
        date_from: formData.date_from,
        date_to: formData.date_to,
        accounts: accountMode === 'list' ? selectedAccounts.map(a => a.id) : [],
        account_from: accountMode === 'range' ? accountRange.from : '',
        account_to: accountMode === 'range' ? accountRange.to : '',
        third_id: selectedThird?.id || formData.third_id || '',
        document_types: selectedTypes,
        tax_type: selectedTaxType,
        include_thirds_toggle: includeThirds,
        month,
        year,
        utility_account: utilityAccount?.account,
        product_ids: selectedProducts.map(p => p.id)
      };
      const generatedJson = await generateAccountingReport(requestData);
      setJsonContent(generatedJson.data);
    } catch (err) {
      setError('Error al generar el reporte.');
      console.error('Error generating report:', err);
    } finally {
      setIsLoading(false);
      dispatchCustomEvent("loading-btn", {});
    }
  };

  const handleBackToForm = () => {
    setJsonContent(null);
    setError(null);
  };

  const handleModalOpen = () => {
    fetchReports();
    setJsonContent(null);
    setError(null);
  };

  const memoizedFilters = React.useMemo(() => {
    return Object.values(reports?.filter((report) => report.id === formData.report_code)[0]?.filters ?? []);
  }, [reports, formData.report_code]);

  return (
    <ModalBtn
      id="acountig-reports"
      title="Reportes Contables"
      icon={<BarChart3 size={20} />}
      onClick={handleModalOpen}
      style={{ width: '65vw', maxWidth: '95vw' }}
    >
      <div className="new-reports-container">
        {/* Left Column (30%) */}
        <div className="new-reports-sidebar">
          <h5 className="new-reports-section-title">Reportes Contables</h5>
          <p className="new-reports-section-desc">
            Genera reportes y estados financieros detallados para el análisis contable y fiscal de tu negocio. Puedes exportar la información en formato PDF y Excel.
          </p>
        </div>

        {/* Right Column (70%) */}
        <div className="new-reports-form-right">
          {isLoading && <LoadingView />}
          {error && <p className="error-message mb-3">{error}</p>}

          <div>
            {jsonContent ? (
              <ReportDataView
                data={jsonContent}
                onDownloadExcel={() => downloadAccountingExcel(jsonContent)}
                onDownloadPdf={() => downloadAccountingPdf(jsonContent)}
                onBack={handleBackToForm}
              />
            ) : (
              <Form 
                onSubmit={(e) => e.preventDefault()} 
                style={{ width: '100%', outline: 'none', display: 'block' }}
              >
                <div className="new-reports-fields-row" style={{ display: 'flex', gap: '20px', width: '100%' }}>
                  <Form.Group style={{ flex: 1 }}>
                    <label className="reports-label">Seleccionar Reporte</label>
                    <Form.Select
                      id="report-select"
                      value={formData.report_code}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => {
                          const updated = { ...prev, report_code: val };
                          if (val === 'daily_closure') {
                            updated.date_to = prev.date_from;
                          }
                          return updated;
                        });
                      }}
                      disabled={isLoading}
                    >
                      <option value="">Seleccionar Reporte</option>
                      {reports.map((report) => (
                        <option key={report.id} value={report.id}>
                          {report.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <hr className="new-reports-separator" />

                <div className="new-reports-fields-row" style={{ display: (formData.report_code === 'balance_sheet') ? 'none' : 'flex', gap: '20px', width: '100%' }}>
                  {formData.report_code === 'daily_closure' ? (
                    <Form.Group style={{ flex: 1 }}>
                      <label className="reports-label">Fecha del Reporte</label>
                      <DateSelector
                        date={formData.date_from}
                        onChange={(value) => {
                          const formatted = value.format('YYYY-MM-DD');
                          setFormData(prev => ({ ...prev, date_from: formatted, date_to: formatted }));
                        }}
                      />
                    </Form.Group>
                  ) : (
                    <>
                      <Form.Group style={{ flex: 1 }}>
                        <label className="reports-label">Fecha Desde</label>
                        <DateSelector
                          date={formData.date_from}
                          onChange={(value) => setFormData(prev => ({ ...prev, date_from: value.format('YYYY-MM-DD') }))}
                        />
                      </Form.Group>
                      <Form.Group style={{ flex: 1 }}>
                        <label className="reports-label">Fecha Hasta</label>
                        <DateSelector
                          date={formData.date_to}
                          onChange={(value) => setFormData(prev => ({ ...prev, date_to: value.format('YYYY-MM-DD') }))}
                        />
                      </Form.Group>
                    </>
                  )}
                </div>

                {formData.report_code && <hr className="new-reports-separator" />}

                {Object.values(memoizedFilters ?? [])?.map(key => {
                  return (
                    <div key={key} style={{ marginBottom: '16px' }}>
                      <DynamicForm componentName={key} componentProps={componentsMap[key]} />
                    </div>
                  );
                })}

                <div className="reports-footer">
                  <ButtonBasic
                    title="Cerrar"
                    type="option"
                    onClick={() => dispatchCustomEvent('close-modal', {})}
                  />
                  <ButtonPrimary
                    id="btn-generate-report"
                    title="Generar reporte"
                    onClick={handleGenerateReport}
                    showLoading={true}
                  />
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </ModalBtn>
  );
};

export default AccountingReportsModalButton;
